'use client';
import { useEffect, useState } from 'react';
import { Heading } from './heading';
import { Subheading } from './subheading';
import { BlurImage } from './blur-image';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

export const Companies = () => {
  const { t, i18n } = useTranslation('common');

  let [logos, setLogos] = useState([
    [
      {
        title: 'amazon',
        src: '/logos/amazon.png',
      },
      {
        title: 'circle8',
        src: '/logos/circle8.webp',
      },
      {
        title: 'ant',
        src: '/logos/ant.png',
      },
      {
        title: 'beet',
        src: '/logos/beet.png',
      },
    ],
    [
      {
        title: 'greentech',
        src: '/logos/greentech.jpg',
      },
      {
        title: 'mm1',
        src: '/logos/mm1.png',
      },
      {
        title: 'navfilm',
        src: '/logos/navfilm.png',
      },
      {
        title: 'ejaso',
        src: '/logos/ejaso.png',
      },
    ],
  ]);
  const [activeLogoSet, setActiveLogoSet] = useState(logos[0]);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);

  const flipLogos = () => {
    setLogos(currentLogos => {
      const newLogos = [...currentLogos.slice(1), currentLogos[0]];
      setActiveLogoSet(newLogos[0]);
      setIsAnimating(true);
      return newLogos;
    });
  };

  useEffect(() => {
    // Only set up the timer once when the component mounts
    const timer = setTimeout(() => {
      if (!isAnimating) {
        flipLogos();
      }
    }, 5000); // Increase to 5 seconds to reduce frequency

    return () => clearTimeout(timer); // Clear timeout if component unmounts
  }, [isAnimating]);

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      // Force a re-render when language changes
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 10);
    };

    window.addEventListener('languageChanged', handleLanguageChange);

    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, []);

  return (
    <div className="relative z-20 py-10 md:py-40    ">
      <Heading as="h2">{t('homepage.companies.title')}</Heading>
      <Subheading className="text-center ">{t('homepage.companies.subtitle')}</Subheading>

      <div className="flex gap-10 flex-wrap justify-center md:gap-40 relative h-full w-full mt-10 ">
        <AnimatePresence
          mode="popLayout"
          onExitComplete={() => {
            setIsAnimating(false);
          }}
        >
          {activeLogoSet.map((logo, idx) => (
            <motion.div
              initial={{
                y: 40,
                opacity: 0,
                filter: 'blur(10px)',
              }}
              animate={{
                y: 0,
                opacity: 1,
                filter: 'blur(0px)',
              }}
              exit={{
                y: -40,
                opacity: 0,
                filter: 'blur(10px)',
              }}
              transition={{
                duration: 0.8,
                delay: 0.1 * idx,
                ease: [0.4, 0, 0.2, 1],
              }}
              key={logo.title}
              className="relative"
            >
              <div className="flex items-center justify-center h-16 md:h-20 w-32 md:w-40 bg-white dark:bg-gray-800 rounded-lg shadow-sm p-2 hover:shadow-md transition-shadow duration-200">
                <BlurImage
                  src={logo.src}
                  alt={logo.title}
                  className="max-h-full max-w-full object-contain rounded-lg dark:brightness-110"
                  style={{ objectFit: 'contain' }}
                />
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};
